import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AddDashboardDialog } from './add-dashboard-dialog';
import { Dashboard } from '../../../features/dashboard/models/dashboard.model';
import { DashboardService } from '../../../features/dashboard/services/dashboard/dashboard.service';
import { ProjectService } from '../../../shared/services/project/project.service';
import { ErrorService } from '../../../shared/services/error/error.service';

const DASHBOARD_ROUTE = '/dashboard';

/**
 * Creating a dashboard: the add dialog, then save and open it. Shared by the
 * sidebar's "+" and the command palette. Callers gate it on `edit` on the
 * Dashboards section.
 */
@Injectable({ providedIn: 'root' })
export class AddDashboardService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);
  private readonly projectService = inject(ProjectService);
  private readonly errorService = inject(ErrorService);

  open(): void {
    const currentProject = this.projectService.currentProject();
    if (!currentProject) return;

    this.dialog
      .open(AddDashboardDialog, {
        data: { isOverview: false, currentProject },
      })
      .afterClosed()
      .subscribe(async (dashboard?: Dashboard) => {
        if (!dashboard?.name) return;
        await this.saveAndRedirect(dashboard);
      });
  }

  async saveAndRedirect(dashboard: Dashboard): Promise<void> {
    try {
      const saved = await firstValueFrom(
        this.dashboardService.saveLayout(dashboard),
      );
      this.dashboardService.selectedDashboardID.set(saved.id);
      await this.router.navigate([DASHBOARD_ROUTE, `${saved.id}`]);
    } catch (err) {
      this.errorService.report(err, {
        source: 'AddDashboardService',
        action: 'saveAndRedirect',
      });
    }
  }
}
